/* ============================================================
   useReviewSnapshot — immutable thread context for a shared plan

   A revised plan may carry one encrypted review snapshot seeded by the
   agent: the prior threads, its replies, and their open/resolved state.
   The browser reads that snapshot once when the plan opens. Human edits stay
   local until the reviewer explicitly copies feedback back to the agent.

   There is deliberately no polling, autosave, merge, or conditional update
   path here. The review snapshot is context, not a collaborative room.
   ============================================================ */

import type { Comment, StoredReview } from '@/types'
import { base64urlDecode, decodeReviewBlob } from '@/crypto'

export async function fetchReviewSnapshot(
  slug: string,
  key: Uint8Array,
): Promise<Comment[]> {
  const response = await fetch(`/api/review/${encodeURIComponent(slug)}`)
  if (response.status === 204 || response.status === 404) return []
  if (!response.ok) throw new Error(`review snapshot GET ${response.status}`)

  const stored = (await response.json()) as StoredReview
  if (typeof stored.ciphertext !== 'string') {
    throw new Error('review snapshot is malformed')
  }

  const review = await decodeReviewBlob(key, base64urlDecode(stored.ciphertext))
  return review.comments
}
